import type { AttendanceRow } from '../../../shared/api/attendance'

/**
 * The register's draft arithmetic, kept out of the component so it can be
 * tested — the same split `attendanceMonth.ts` makes for the calendar.
 *
 * 🔴 THIS IS THE ONE SCREEN WHERE A WRONG SET MESSAGES A FAMILY. Saving posts
 * the whole absent set and the server enqueues a WhatsApp message for each
 * child newly in it; a WhatsApp message cannot be recalled. So "which children
 * are marked", "has anything actually changed", and "would this save reach
 * everyone" are three questions that must be answerable without rendering a
 * component, and all three were previously inline expressions nobody could test.
 *
 * 🔴 EVERY FUNCTION HERE IS PURE AND RETURNS THE INPUT UNCHANGED WHEN NOTHING
 * CHANGED — that identity is what lets React skip a re-render and, more
 * importantly, what keeps `hasChanges` from lighting Save on a register nobody
 * edited.
 */

/** The children the server currently has marked absent today. */
export function absentFrom(rows: readonly AttendanceRow[]): Set<string> {
  return new Set(rows.filter((row) => row.absent).map((row) => row.playerId))
}

/**
 * Set one child's mark. **Not** a toggle.
 *
 * 🔴 IDEMPOTENT, AND THAT IS THE POINT. A teacher who is unsure whether her tap
 * landed taps again; under a toggle that second tap silently reverses the first
 * and — on save — either messages a parent who should not have been messaged or
 * fails to message one who should. Marking a child present who is already
 * present is a no-op here, and returns the very same set so nothing downstream
 * treats it as an edit.
 */
export function applyMark(
  absent: ReadonlySet<string>,
  playerId: string,
  isAbsent: boolean,
): ReadonlySet<string> {
  if (absent.has(playerId) === isAbsent) return absent
  const next = new Set(absent)
  if (isAbsent) next.add(playerId)
  else next.delete(playerId)
  return next
}

/**
 * Do these two registers say the same thing?
 *
 * ⚠️ CONTENT, NOT IDENTITY. The screen's "is Save worth offering" test used to
 * be *"has the draft been touched at all"*, so marking a child absent and
 * putting them back left Save lit on a register identical to the stored one.
 */
export function sameMarks(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a === b) return true
  if (a.size !== b.size) return false
  for (const id of a) if (!b.has(id)) return false
  return true
}

/** Is there anything here the server does not already have? */
export function hasChanges(
  draft: ReadonlySet<string> | null,
  stored: ReadonlySet<string>,
): boolean {
  return draft !== null && !sameMarks(draft, stored)
}

/**
 * How many children this save would record as absent but tell nobody about.
 *
 * 🔴 COUNTED OVER WHAT IS MARKED, NOT OVER THE CLASS. `parentReachable` is
 * false for a child with no linked parent, a deactivated one, one with no phone,
 * or one who has not opted into WhatsApp — and a teacher marking a child absent
 * reasonably assumes the parent is told. Counting the whole roster would put a
 * standing roster complaint above the save button on every day including the
 * ones where none of those children is absent, and a warning that is always
 * there is a warning nobody reads on the day it matters.
 *
 * ⚠️ REACHABILITY IS NOT "HAS A NAME". A child can have `parentName` set and
 * still be unreachable, so this never infers it from the name column.
 */
export function unreachableAbsentCount(
  rows: readonly AttendanceRow[],
  absent: ReadonlySet<string>,
): number {
  return rows.filter((row) => absent.has(row.playerId) && !row.parentReachable).length
}
